import React, { createContext, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import API_BASE_URL from '../config';
import logger from '../utils/logger';

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [locationUpdates, setLocationUpdates] = useState([]);
  const [alarms, setAlarms] = useState([]);
  const [statusUpdates, setStatusUpdates] = useState([]);
  const [systemAlerts, setSystemAlerts] = useState([]);
  const [analyticsNotifications, setAnalyticsNotifications] = useState([]);
  const { isAuthenticated, user } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      // Connect to socket server
      // Use explicit path and allow fallback to polling if websocket fails
      const newSocket = io(API_BASE_URL, {
        path: '/socket.io',
        transports: ['websocket', 'polling'],
        autoConnect: true,
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: Infinity
      });

      newSocket.on('connect', () => {
        logger.log('Connected to WebSocket server');
        setConnected(true);
        
        // Join tenant room for analytics notifications
        if (user?.tenant_id) {
          newSocket.emit('join-tenant', user.tenant_id);
        }
      });

      newSocket.on('disconnect', () => {
        logger.log('Disconnected from WebSocket server');
        setConnected(false);
      });

      newSocket.on('connect_error', (error) => {
        logger.error('Socket connection error:', error);
        setConnected(false);
      });

      // Listen for location updates
      newSocket.on('location-update', (data) => {
        logger.log('Location update received:', data);
        setLocationUpdates(prev => [data, ...prev.slice(0, 99)]); // Keep last 100 updates
      });

      // Listen for alarms
      newSocket.on('alarm', (data) => {
        logger.log('Alarm received:', data);
        setAlarms(prev => [data, ...prev.slice(0, 49)]); // Keep last 50 alarms
      });

      // Listen for status updates
      newSocket.on('status-update', (data) => {
        logger.log('Status update received:', data);
        setStatusUpdates(prev => [data, ...prev.slice(0, 99)]); // Keep last 100 updates
      });

      // Listen for analytics notifications
      newSocket.on('analytics-notification', (data) => {
        logger.log('Analytics notification received:', data);
        setAnalyticsNotifications(prev => [data, ...prev.slice(0, 49)]); // Keep last 50 notifications
        
        // Show browser notification for critical and high notifications
        if (data.severity === 'critical' || data.severity === 'high') {
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification(data.title, {
              body: data.message,
              icon: '/icon-192x192.png',
              badge: '/icon-192x192.png',
              tag: `analytics-notification-${data.id}`,
              requireInteraction: data.severity === 'critical'
            });
          }
        }
      });

      // Listen for system alerts (monitoring alerts)
      newSocket.on('system-alert', (data) => {
        logger.log('System alert received:', data);
        setSystemAlerts(prev => [data, ...prev.slice(0, 49)]); // Keep last 50 alerts
        
        // Show browser notification for critical and high alerts
        if (data.severity === 'critical' || data.severity === 'high') {
          if ('Notification' in window) {
            if (Notification.permission === 'granted') {
              new Notification(`🚨 ${data.severity.toUpperCase()} Alert`, {
                body: data.message,
                icon: '/favicon.ico',
                tag: data.id, // Prevent duplicate notifications
                requireInteraction: data.severity === 'critical'
              });
            } else if (Notification.permission === 'default') {
              // Request permission on first critical/high alert
              Notification.requestPermission().then(permission => {
                if (permission === 'granted') {
                  new Notification(`🚨 ${data.severity.toUpperCase()} Alert`, {
                    body: data.message,
                    icon: '/favicon.ico',
                    tag: data.id,
                    requireInteraction: data.severity === 'critical'
                  });
                }
              });
            }
          }
        }
      });

      // Join admin alerts room if user is superadmin
      if (user && user.role === 'superadmin') {
        newSocket.emit('join-admin-alerts');
      }

      setSocket(newSocket);

      return () => {
        newSocket.close();
      };
    } else {
      // Disconnect socket if not authenticated
      if (socket) {
        socket.close();
        setSocket(null);
        setConnected(false);
      }
    }
  // socket lifecycle is tied to auth; tenant changes trigger a full reload (TenantContext)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const joinVehicleTracking = (vehicleId) => {
    if (socket && connected) {
      socket.emit('join-vehicle-tracking', vehicleId);
    }
  };

  const joinFleetTracking = () => {
    if (socket && connected && user?.tenant_id) {
      socket.emit('join-fleet-tracking', user.tenant_id);
    }
  };

  const leaveVehicleTracking = (vehicleId) => {
    if (socket && connected) {
      socket.emit('leave-vehicle-tracking', vehicleId);
    }
  };

  const leaveFleetTracking = () => {
    if (socket && connected) {
      socket.emit('leave-fleet-tracking');
    }
  };

  const joinAdminAlerts = () => {
    if (socket && connected && user?.role === 'superadmin') {
      socket.emit('join-admin-alerts');
    }
  };

  const value = {
    socket,
    connected,
    locationUpdates,
    alarms,
    statusUpdates,
    systemAlerts,
    analyticsNotifications,
    joinVehicleTracking,
    joinFleetTracking,
    leaveVehicleTracking,
    leaveFleetTracking,
    joinAdminAlerts
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};















